/** Shared authentication. A role is chosen only when registering a new account. */
import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { config, isConfigured } from '@/lib/config'
import type { Avatar as AvatarData, Role } from '@/lib/types'
import { AvatarPicker } from '@/components/Avatar'
import { AreaPicker, ProgramChips } from '@/lib/programs'
import { PENDING_PROFILE_KEY } from '@/auth/ProfileContext'

type Mode = 'login' | 'signup' | 'confirm' | 'code' | 'reset'

export default function AuthPage({ role = 'sme', mode: initial, embedded = false }: { role?: Role; mode: Mode; embedded?: boolean }) {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation() as { state?: { from?: string } }
  const [mode, setMode] = useState<Mode>(initial)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [name, setName] = useState('')
  const [bio, setBio] = useState('')
  const [avatar, setAvatar] = useState<AvatarData | null>(null)
  const [areas, setAreas] = useState<string[]>([])
  const [programs, setPrograms] = useState<string[]>([])
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const base = role === 'sme' ? '/sme' : '/eng'
  const Container = embedded ? 'div' : 'main'
  const finishSignIn = () => navigate('/', { replace: true, state: location.state })

  function switchMode(next: Mode) {
    setMode(next); setCodeSent(false); setCode(''); setPassword(''); setNewPassword(''); setConfirmPassword('')
    setError(null); setInfo(null)
  }

  async function resendResetCode() {
    if (busy) return
    setBusy(true); setError(null); setInfo(null)
    try {
      await auth.requestPasswordReset(email)
      setCode('')
      setInfo('A new reset code has been sent. Use the most recent email and check your spam folder.')
    } catch (error) { setError((error as Error).message) }
    finally { setBusy(false) }
  }

  async function submit(e: FormEvent) {
    e.preventDefault(); if (busy) return; setError(null); setInfo(null); setBusy(true)
    try {
      if (mode === 'login') {
        await auth.signIn(email, password)
        finishSignIn()
      } else if (mode === 'code') {
        if (!codeSent) {
          await auth.signInWithCode(email)
          setCodeSent(true)
          setInfo('We emailed you a one-time code. If it has not arrived after a minute, check your spam folder.')
        } else {
          await auth.confirmSignInCode(code)
          finishSignIn()
        }
      } else if (mode === 'reset') {
        if (!codeSent) {
          const address = email.trim().toLowerCase()
          await auth.requestPasswordReset(address)
          setEmail(address); setCodeSent(true)
          setInfo('If your account is eligible for password recovery, a reset code will arrive by email. Check your spam folder if it has not arrived after a minute.')
        } else {
          if (newPassword !== confirmPassword) throw new Error('The passwords do not match.')
          await auth.completePasswordReset(email, code, newPassword)
          switchMode('login')
          setInfo('Password updated. Sign in with your new password.')
        }
      } else if (mode === 'signup') {
        if (role === 'engineer' && !email.toLowerCase().endsWith(`@${config.engineerDomain}`)) throw new Error(`Engineer accounts need an @${config.engineerDomain} email address.`)
        if (role === 'sme') {
          if (!avatar) throw new Error('Please choose a profile picture or an emoji.')
          if (bio.trim().length < 20) throw new Error('Please write a short bio (at least a sentence).')
          if (!areas.length) throw new Error('Choose at least one area or profession.')
          if (programs.length === 0) throw new Error('Please select at least one program you work in.')
          // Saved to the profile after the first sign-in (Layout), since the API needs a token.
          sessionStorage.setItem(PENDING_PROFILE_KEY, JSON.stringify({ name, bio: bio.trim(), avatar, programs, areas }))
        }
        const next = await auth.signUp(email, password, name, role)
        if (next === 'confirm') { setMode('confirm'); setInfo('We sent a confirmation code to your email. If it has not arrived after a minute, check your spam folder.') } else { await auth.signIn(email, password); finishSignIn() }
      } else {
        await auth.confirm(email, code)
        if (password) {
          await auth.signIn(email, password)
          finishSignIn()
        } else {
          setMode('code'); setCode(''); setCodeSent(false)
          setInfo('Your email is confirmed. Request a one-time code below to sign in.')
        }
      }
    } catch (err) {
      setError((err as Error).message)
    } finally { setBusy(false) }
  }

  return (
    <Container className={embedded ? 'auth-page auth-embedded' : 'page narrow auth-page'} style={embedded ? undefined : { maxWidth: mode === 'signup' && role === 'sme' ? 560 : 440 }}>
      {!embedded && <Link className="backlink" to="/">← Back</Link>}
      {!isConfigured && <div className="note warn">Backend not configured; sign-in will not work. See docs/deployment.md.</div>}
      <form className="box" onSubmit={submit} aria-busy={busy}>
        <fieldset disabled={busy} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
        <h1 className="auth-title">{mode === 'login' ? 'Sign in' : mode === 'signup' ? 'Create account' : mode === 'code' ? 'Sign in with a code' : mode === 'reset' ? 'Reset your password' : 'Confirm your email'}</h1>
        {mode === 'signup' && <div className="auth-account-type" role="group" aria-label="Account type">
          <Link to="/sme/signup" aria-current={role === 'sme' ? 'page' : undefined} aria-disabled={busy} onClick={e => { if (busy) e.preventDefault() }}>Expert</Link>
          <Link to="/eng/signup" aria-current={role === 'engineer' ? 'page' : undefined} aria-disabled={busy} onClick={e => { if (busy) e.preventDefault() }}>Developer</Link>
        </div>}
        {mode === 'reset' && !codeSent && <p className="muted small">Enter the email of your existing account. We’ll send a code so you can choose a new password.</p>}
        {mode === 'code' && !codeSent && <p className="muted small">No password needed. We email a one-time code to the address of your existing account.</p>}
        {mode === 'code' && !codeSent && <div className="note warn small">Only for <strong>confirmed</strong> accounts. If you created your account but never entered the confirmation code we emailed you, <button className="link" type="button" onClick={() => switchMode('confirm')}>confirm your email first</button>; a sign-in code will otherwise be rejected.</div>}
        {mode === 'signup' && <label className="field"><span>Name</span><input type="text" value={name} onChange={(e) => setName(e.target.value)} required /></label>}
        {mode === 'signup' && role === 'sme' && (
          <>
            <div className="field"><span className="field-label">Profile picture</span><em className="hint">A photo or any emoji; developers see it next to your name.</em><AvatarPicker value={avatar} onChange={setAvatar} name={name} /></div>
            <label className="field"><span>Short bio</span><em className="hint">Who you are professionally and what you work on. Two or three sentences.</em>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} maxLength={600} required style={{ minHeight: 80 }} /></label>
            <div className="field"><span className="field-label">Areas / professions</span><em className="hint">Choose up to five areas that describe your work.</em><AreaPicker value={areas} onChange={setAreas} /></div>
            <div className="field"><span className="field-label">Software and tools</span><em className="hint">Choose the programs you know. Add a name if it is missing.</em><ProgramChips value={programs} onChange={setPrograms} /></div>
          </>
        )}
        <label className="field"><span>Email</span><input type="email" autoComplete="username" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} required disabled={codeSent} /></label>
        {mode === 'code' && codeSent && <label className="field"><span>One-time code</span><em className="hint">The email is titled “Seldon Tasks: your code is …”. Check your spam folder if it has not arrived after a minute.</em><input type="text" value={code} onChange={(e) => setCode(e.target.value)} required inputMode="numeric" autoFocus /></label>}
        {(mode === 'login' || mode === 'signup') && <label className="field"><span>Password</span><input type="password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} />
          {mode === 'signup' && <em className="hint">At least 10 characters with upper and lower case letters and a number.</em>}</label>}
        {mode === 'reset' && codeSent && <>
          <label className="field"><span>Reset code</span><input type="text" value={code} onChange={e => setCode(e.target.value)} required inputMode="numeric" autoComplete="one-time-code" autoFocus /></label>
          <label className="field"><span id="new-password-label">New password</span><input aria-labelledby="new-password-label" aria-describedby="new-password-hint" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={10} autoComplete="new-password" />
            <em id="new-password-hint" className="hint">At least 10 characters with upper and lower case letters and a number.</em></label>
          <label className="field"><span>Confirm new password</span><input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={10} autoComplete="new-password" /></label>
        </>}
        {mode === 'confirm' && <label className="field"><span>Confirmation code</span><em className="hint">The code from the “Seldon Tasks: your code is …” email sent at sign-up (check spam). Lost it? Use “Resend code” below.</em><input type="text" value={code} onChange={(e) => setCode(e.target.value)} required inputMode="numeric" /></label>}
        {info && <div className="note ok" role="status">{info}</div>}
        {error && <div className="note err" role="alert">{error}</div>}
        <div className="auth-actions">
          <button className="primary" disabled={busy || !isConfigured}>{busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : mode === 'signup' ? 'Create account' : mode === 'code' ? (codeSent ? 'Sign in' : 'Email me a code') : mode === 'reset' ? (codeSent ? 'Set new password' : 'Email me a reset code') : 'Confirm'}</button>
          {(mode === 'code' || mode === 'reset') && codeSent && <button type="button" className="ghost" onClick={() => switchMode(mode)}>Use a different email</button>}
          {mode === 'reset' && codeSent && <button type="button" className="ghost" onClick={resendResetCode}>Resend reset code</button>}
          {mode === 'confirm' && <button type="button" className="ghost" onClick={() => auth.resendCode(email).then(() => setInfo('Code re-sent.')).catch((e) => setError(e.message))}>Resend code</button>}
        </div>
        </fieldset>
        <div className="auth-links small">
          {mode === 'login' ? <>
            <Link to={`${base}/signup`} aria-disabled={busy} onClick={e => { if (busy) e.preventDefault() }}>Create account</Link>
            <button className="link" type="button" disabled={busy} onClick={() => switchMode('code')}>Sign in with a code</button>
            <button className="link" type="button" disabled={busy} onClick={() => switchMode('reset')}>Reset password</button>
          </> : <Link to="/login" aria-disabled={busy} onClick={e => { if (busy) e.preventDefault(); else switchMode('login') }}>Sign in with password</Link>}
          {mode === 'reset' && <button className="link" type="button" disabled={busy} onClick={() => switchMode('code')}>Sign in with a code</button>}
          {mode !== 'confirm' && <button className="link" type="button" disabled={busy} onClick={() => switchMode('confirm')}>Confirm your email</button>}
        </div>
      </form>
    </Container>
  )
}
